import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  GripVertical, Trash2, Plus, Video, Paperclip, X, ChevronLeft,
  Eye, EyeOff, FileText, Globe, Users, Crown, Lock, ListOrdered, Clock,
} from 'lucide-react';

function MarkdownGuide() {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button 
        type="button" 
        className="btn btn-ghost btn-sm" 
        style={{ fontSize: 11, padding: '2px 8px', height: 'auto', color: 'var(--accent)', alignSelf: 'flex-start', border: '1px dashed var(--accent)', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }} 
        onClick={() => setOpen(true)}
      >
        💡 Formatting Help (Markdown Guide)
      </button>
    );
  }

  return (
    <div style={{ 
      background: 'var(--surface2)', 
      border: '1px solid var(--border)', 
      borderRadius: 8, 
      padding: 12, 
      fontSize: 12, 
      color: 'var(--text2)',
      marginTop: 6
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ color: 'var(--text)', fontSize: 12 }}>Markdown Guide</strong>
        <button 
          type="button" 
          className="btn btn-ghost btn-sm" 
          style={{ height: 'auto', padding: '2px 6px', fontSize: 11 }} 
          onClick={() => setOpen(false)}
        >
          Hide
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
        <div>
          <strong style={{ color: 'var(--text)' }}>Headers:</strong><br />
          <code># Title</code><br />
          <code>## Subtitle</code>
        </div>
        <div>
          <strong style={{ color: 'var(--text)' }}>Styles:</strong><br />
          <code>**bold**</code> or <code>*italics*</code>
        </div>
        <div>
          <strong style={{ color: 'var(--text)' }}>Lists:</strong><br />
          <code>- Bullet item</code><br />
          <code>1. Numbered item</code>
        </div>
        <div>
          <strong style={{ color: 'var(--text)' }}>Links & Code:</strong><br />
          <code>[text](url)</code><br />
          <code>`inline code`</code>
        </div>
      </div>
      <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
        <strong style={{ color: 'var(--text)' }}>Code Blocks:</strong><br />
        <pre style={{ margin: '4px 0 0 0', background: 'var(--surface)', padding: 6, borderRadius: 4, fontSize: 10, fontFamily: 'monospace', color: 'var(--text)' }}>
{"```javascript\nconst a = 12;\nconsole.log(a);\n```"}
        </pre>
      </div>
    </div>
  );
}

export default function CourseStudio({ course, onBack, onToast }) {
  const { API, token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', ...authHeaders };

  const [modules, setModules]         = useState([]);
  const [lessons, setLessons]         = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);

  // Course settings (editable header)
  const [title, setTitle]             = useState(course.title || '');
  const [description, setDescription] = useState(course.description || '');
  const [isPublished, setIsPublished] = useState(course.isPublished !== false);
  const [accessType, setAccessType]   = useState(course.accessType || 'free');
  const [price, setPrice]             = useState(course.price ?? 0);
  const [requiredLevel, setReqLevel]  = useState(course.requiredLevel || 1);
  const [sequentialUnlock, setSequential] = useState(!!course.sequentialUnlock);
  const [dripEnabled, setDrip]        = useState(!!course.dripEnabled);

  // Inline add/edit form state
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [addingLessonTo, setAddingLessonTo] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);

  const [coverImageUrl, setCoverImageUrl] = useState(course.coverImageUrl || '');
  
  const coverFileInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const drag = useRef(null);          // { type:'module'|'lesson', id, moduleId }

  useEffect(() => { fetchCurriculum(); /* eslint-disable-next-line */ }, [course.id]);

  async function fetchCurriculum() {
    try {
      const res = await fetch(`${API}/api/courses/${course.id}/curriculum`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setModules(data.modules || []);
      setLessons(data.lessons || []);
      setAttachments(data.attachments || []);
    } catch (e) {
      onToast('Failed to load curriculum: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Course settings ──────────────────────────────────────────────
  async function saveCourse(patch) {
    try {
      const res = await fetch(`${API}/api/courses/${course.id}`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Save failed');
      onToast('Course updated');
    } catch (e) { onToast(e.message); }
  }

  async function deleteCourse() {
    if (!confirm('Delete this entire course and all its lessons? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/courses/${course.id}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error('Delete failed');
      onToast('Course deleted');
      onBack();
    } catch (e) { onToast(e.message); }
  }

  // ── Modules ──────────────────────────────────────────────────────
  async function createModule(e) {
    e.preventDefault();
    if (!newModuleTitle.trim()) return;
    const res = await fetch(`${API}/api/courses/${course.id}/modules`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ title: newModuleTitle.trim(), sortOrder: modules.length }),
    });
    if (res.ok) { setNewModuleTitle(''); fetchCurriculum(); }
    else onToast('Error creating module');
  }

  async function renameModule(moduleId, newTitle) {
    setEditingModuleId(null);
    if (!newTitle.trim()) return;
    setModules(prev => prev.map(m => m.id === moduleId ? { ...m, title: newTitle } : m));
    await fetch(`${API}/api/courses/${course.id}/modules/${moduleId}`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ title: newTitle.trim() }),
    });
  }

  async function deleteModule(moduleId) {
    if (!confirm('Delete this module and all its lessons?')) return;
    const res = await fetch(`${API}/api/courses/${course.id}/modules/${moduleId}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) { if (activeLesson && lessons.find(l => l.id === activeLesson.id)?.moduleId === moduleId) setActiveLesson(null); fetchCurriculum(); }
    else onToast('Error deleting module');
  }

  // ── Lessons ──────────────────────────────────────────────────────
  async function createLesson(e, moduleId) {
    e.preventDefault();
    if (!newLessonTitle.trim()) return;
    const count = lessons.filter(l => l.moduleId === moduleId).length;
    const res = await fetch(`${API}/api/courses/${course.id}/modules/${moduleId}/lessons`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ title: newLessonTitle.trim(), sortOrder: count }),
    });
    if (res.ok) { setNewLessonTitle(''); setAddingLessonTo(null); fetchCurriculum(); }
    else onToast('Error creating lesson');
  }

  async function saveLesson(lessonId, updates) {
    const res = await fetch(`${API}/api/courses/${course.id}/lessons/${lessonId}`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify(updates),
    });
    if (res.ok) {
      setLessons(prev => prev.map(l => l.id === lessonId ? { ...l, ...updates } : l));
      setActiveLesson(a => a && a.id === lessonId ? { ...a, ...updates } : a);
      onToast('Lesson saved');
    } else onToast('Error saving lesson');
  }

  async function deleteLesson(lessonId) {
    if (!confirm('Delete this lesson?')) return;
    const res = await fetch(`${API}/api/courses/${course.id}/lessons/${lessonId}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) {
      if (activeLesson?.id === lessonId) setActiveLesson(null);
      fetchCurriculum();
    } else onToast('Error deleting lesson');
  }

  // ── Drag & drop reorder ──────────────────────────────────────────
  function persistOrder(nextModules, nextLessons) {
    const lessonPayload = [];
    nextModules.forEach(m => {
      nextLessons.filter(l => l.moduleId === m.id).forEach((l, i) => lessonPayload.push({ id: l.id, moduleId: m.id, sortOrder: i }));
    });
    fetch(`${API}/api/courses/${course.id}/reorder`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ modules: nextModules.map((m, i) => ({ id: m.id, sortOrder: i })), lessons: lessonPayload }),
    }).catch(() => onToast('Failed to save order'));
  }

  function onDropModule(targetModuleId) {
    const d = drag.current;
    if (!d || d.type !== 'module' || d.id === targetModuleId) return;
    const arr = [...modules];
    const from = arr.findIndex(m => m.id === d.id);
    const to = arr.findIndex(m => m.id === targetModuleId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setModules(arr);
    persistOrder(arr, lessons);
    drag.current = null;
  }

  function onDropLesson(targetModuleId, targetLessonId) {
    const d = drag.current;
    if (!d || d.type !== 'lesson') return;
    const arr = [...lessons];
    const di = arr.findIndex(l => l.id === d.id);
    if (di < 0) return;
    const [moved] = arr.splice(di, 1);
    moved.moduleId = targetModuleId;
    if (targetLessonId == null) {
      let insertAt = arr.length;
      for (let i = arr.length - 1; i >= 0; i--) { if (arr[i].moduleId === targetModuleId) { insertAt = i + 1; break; } }
      arr.splice(insertAt, 0, moved);
    } else {
      const ti = arr.findIndex(l => l.id === targetLessonId);
      arr.splice(ti < 0 ? arr.length : ti, 0, moved);
    }
    setLessons(arr);
    persistOrder(modules, arr);
    drag.current = null;
  }

  // ── File upload (video / attachment) ─────────────────────────────
  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file || !activeLesson) return;
    const isVideo = file.type.startsWith('video/');
    try {
      onToast('Uploading…');
      const ext = '.' + (file.name.split('.').pop() || 'bin');
      const buf = await file.arrayBuffer();
      const up = await fetch(`${API}/api/upload`, {
        method: 'POST', headers: { ...authHeaders, 'X-File-Ext': ext, 'Content-Type': 'application/octet-stream' }, body: buf,
      });
      const data = await up.json();
      if (!up.ok) throw new Error(data.error || 'Upload failed');
      if (isVideo) {
        await saveLesson(activeLesson.id, { videoUrl: data.url });
      } else {
        const att = await fetch(`${API}/api/courses/${course.id}/lessons/${activeLesson.id}/attachments`, {
          method: 'POST', headers: jsonHeaders, body: JSON.stringify({ fileName: file.name, fileUrl: data.url }),
        });
        if (!att.ok) { const d = await att.json(); throw new Error(d.error || 'Attachment failed'); }
        onToast('Attachment added'); fetchCurriculum();
      }
    } catch (err) { onToast(err.message); }
    e.target.value = '';
  }
  async function handleCoverUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      onToast('Uploading cover…');
      const ext = '.' + (file.name.split('.').pop() || 'jpg');
      const buf = await file.arrayBuffer();
      const up = await fetch(`${API}/api/upload`, {
        method: 'POST', 
        headers: { ...authHeaders, 'X-File-Ext': ext, 'Content-Type': 'application/octet-stream' }, 
        body: buf,
      });
      const data = await up.json();
      if (!up.ok) throw new Error(data.error || 'Upload failed');
      setCoverImageUrl(data.url);
      await saveCourse({ coverImageUrl: data.url });
      onToast('Cover image updated');
    } catch (err) { 
      onToast(err.message); 
    }
    e.target.value = '';
  }

  if (loading) return <div className="card" style={{ padding: 24 }}>Loading course studio…</div>;

  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border2)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 14, outline: 'none', fontFamily: 'inherit' };

  return (
    <div className="studio-grid">

      {/* ── Curriculum builder ── */}
      <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 150px)' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack}><ChevronLeft size={16} /> Exit</button>
          <span className="badge" style={{ marginLeft: 'auto' }}>{lessons.length} lessons</span>
        </div>

        <div className="card-body" style={{ overflowY: 'auto', flex: 1 }}>
          {modules.length === 0 && <div className="text-muted" style={{ marginBottom: 12 }}>No modules yet. Add your first module below.</div>}

          {modules.map(mod => (
            <div key={mod.id}
              draggable
              onDragStart={() => { drag.current = { type: 'module', id: mod.id }; }}
              onDragOver={e => e.preventDefault()}
              onDrop={() => onDropModule(mod.id)}
              style={{ marginBottom: 16, background: 'var(--surface2)', borderRadius: 12, padding: 10 }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <GripVertical size={15} style={{ color: 'var(--text3)', cursor: 'grab', flexShrink: 0 }} />
                {editingModuleId === mod.id ? (
                  <input autoFocus defaultValue={mod.title} style={{ ...inputStyle, padding: '5px 8px' }}
                    onBlur={e => renameModule(mod.id, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') renameModule(mod.id, e.target.value); }} />
                ) : (
                  <span style={{ fontWeight: 700, fontSize: 14, flex: 1, cursor: 'text' }} onClick={() => setEditingModuleId(mod.id)}>{mod.title}</span>
                )}
                <button className="qeduvo-icon-btn" style={{ width: 28, height: 28 }} title="Delete module" onClick={() => deleteModule(mod.id)}>
                  <Trash2 size={14} style={{ color: 'var(--accent2)' }} />
                </button>
              </div>

              <div onDragOver={e => e.preventDefault()} onDrop={() => onDropLesson(mod.id, null)} style={{ minHeight: 6 }}>
                {lessons.filter(l => l.moduleId === mod.id).map(les => (
                  <div key={les.id}
                    draggable
                    onDragStart={e => { e.stopPropagation(); drag.current = { type: 'lesson', id: les.id }; }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.stopPropagation(); onDropLesson(mod.id, les.id); }}
                    onClick={() => setActiveLesson(les)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', marginBottom: 4,
                      background: activeLesson?.id === les.id ? 'var(--accent-soft)' : 'var(--surface)',
                      border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer',
                      color: activeLesson?.id === les.id ? 'var(--accent)' : 'var(--text)', fontWeight: activeLesson?.id === les.id ? 600 : 500,
                    }}>
                    <GripVertical size={13} style={{ color: 'var(--text3)', cursor: 'grab', flexShrink: 0 }} />
                    {les.videoUrl ? <Video size={13} style={{ flexShrink: 0, color: 'var(--text3)' }} /> : <FileText size={13} style={{ flexShrink: 0, color: 'var(--text3)' }} />}
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{les.title}</span>
                    <button className="qeduvo-icon-btn" style={{ width: 24, height: 24 }} title="Delete lesson"
                      onClick={e => { e.stopPropagation(); deleteLesson(les.id); }}>
                      <Trash2 size={12} style={{ color: 'var(--text3)' }} />
                    </button>
                  </div>
                ))}
              </div>

              {addingLessonTo === mod.id ? (
                <form onSubmit={e => createLesson(e, mod.id)} style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                  <input autoFocus placeholder="Lesson title" value={newLessonTitle} onChange={e => setNewLessonTitle(e.target.value)} style={{ ...inputStyle, padding: '6px 9px' }} />
                  <button type="submit" className="btn btn-primary btn-sm">Add</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAddingLessonTo(null); setNewLessonTitle(''); }}><X size={14} /></button>
                </form>
              ) : (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 4, color: 'var(--text3)' }} onClick={() => setAddingLessonTo(mod.id)}>
                  <Plus size={14} /> Add lesson
                </button>
              )}
            </div>
          ))}

          <form onSubmit={createModule} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input placeholder="New module title" value={newModuleTitle} onChange={e => setNewModuleTitle(e.target.value)} style={inputStyle} />
            <button className="btn btn-primary btn-sm"><Plus size={14} /></button>
          </form>
        </div>
      </div>

      {/* ── Right: settings + lesson editor ── */}
      <div className="flex-col" style={{ gap: 20 }}>

        {/* Course settings */}
        <div className="card">
          <div className="card-body flex-col" style={{ gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input value={title} onChange={e => setTitle(e.target.value)} onBlur={() => title.trim() && title !== course.title && saveCourse({ title: title.trim() })}
                style={{ ...inputStyle, flex: 1, minWidth: 220, fontSize: 18, fontWeight: 700, border: '1px solid transparent', background: 'transparent', padding: '6px 8px' }} />
              <button className="btn btn-outline btn-sm" onClick={() => { const v = !isPublished; setIsPublished(v); saveCourse({ isPublished: v }); }}>
                {isPublished ? <><Eye size={15} /> Published</> : <><EyeOff size={15} /> Draft</>}
              </button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent2)' }} onClick={deleteCourse}><Trash2 size={15} /> Delete</button>
            </div>
            
            {/* Course Description */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="text-muted" style={{ fontWeight: 600 }}>Course Description</span>
              <textarea 
                defaultValue={description} 
                placeholder="Describe what your students will learn in this course..."
                style={{ ...inputStyle, minHeight: 70, lineHeight: 1.6, resize: 'vertical' }}
                onBlur={e => {
                  const val = e.target.value;
                  if (val !== (course.description || '')) {
                    setDescription(val);
                    saveCourse({ description: val });
                  }
                }} 
              />
              <span className="text-muted" style={{ fontSize: 11, marginTop: -2 }}>
                Supports Markdown (headers, lists, bold, italics, links, and code blocks)
              </span>
              <MarkdownGuide />
            </div>

            {/* Cover Image row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="text-muted" style={{ fontWeight: 600, marginRight: 4 }}>Cover</span>
              <input 
                type="text" 
                value={coverImageUrl || ''} 
                placeholder="Paste cover image URL or upload" 
                style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                onChange={e => { setCoverImageUrl(e.target.value); }} 
                onBlur={() => saveCourse({ coverImageUrl: coverImageUrl.trim() })}
              />
              <button 
                type="button" 
                className="btn btn-outline btn-sm" 
                onClick={() => { coverFileInputRef.current.click(); }}
              >
                Upload Image
              </button>
              <input 
                type="file" 
                ref={coverFileInputRef} 
                style={{ display: 'none' }} 
                accept="image/*" 
                onChange={handleCoverUpload} 
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="text-muted" style={{ fontWeight: 600, marginRight: 4 }}>Access</span>
              {[
                { v: 'free', label: 'Free', icon: Globe },
                { v: 'members', label: 'Members', icon: Users },
                { v: 'premium', label: 'Premium', icon: Crown },
                { v: 'level', label: 'Level', icon: Lock },
              ].map(opt => (
                <button key={opt.v} type="button"
                  className={accessType === opt.v ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                  onClick={() => { setAccessType(opt.v); saveCourse({ accessType: opt.v }); }}>
                  <opt.icon size={14} /> {opt.label}
                </button>
              ))}
              {accessType === 'premium' && (
                <input type="number" min="0" step="0.01" value={price} placeholder="Price $"
                  style={{ ...inputStyle, width: 120 }}
                  onChange={e => setPrice(e.target.value)} onBlur={() => saveCourse({ price: parseFloat(price) || 0 })} />
              )}
              {accessType === 'level' && (
                <input type="number" min="1" value={requiredLevel} placeholder="Level"
                  style={{ ...inputStyle, width: 120 }}
                  onChange={e => setReqLevel(e.target.value)} onBlur={() => saveCourse({ requiredLevel: parseInt(requiredLevel) || 1 })} />
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="text-muted" style={{ fontWeight: 600, marginRight: 4 }}>Unlocking</span>
              <button type="button" className={sequentialUnlock ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                onClick={() => { const v = !sequentialUnlock; setSequential(v); saveCourse({ sequentialUnlock: v }); }}>
                <ListOrdered size={14} /> Sequential
              </button>
              <button type="button" className={dripEnabled ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                onClick={() => { const v = !dripEnabled; setDrip(v); saveCourse({ dripEnabled: v }); }}>
                <Clock size={14} /> Drip content
              </button>
            </div>
          </div>
        </div>

        {/* Lesson editor */}
        <div className="card" style={{ minHeight: 360 }}>
          {activeLesson ? (
            <>
              <div className="card-header"><div className="card-title">Edit lesson</div></div>
              <div className="card-body flex-col" key={activeLesson.id} style={{ gap: 18 }}>
                <div>
                  <label className="form-label" style={{ marginBottom: 6 }}>Title</label>
                  <input defaultValue={activeLesson.title} style={inputStyle}
                    onBlur={e => e.target.value.trim() && e.target.value !== activeLesson.title && saveLesson(activeLesson.id, { title: e.target.value.trim() })} />
                </div>

                <div>
                  <label className="form-label" style={{ marginBottom: 6 }}>Description / content</label>
                  <textarea defaultValue={activeLesson.content || ''} style={{ ...inputStyle, minHeight: 200, lineHeight: 1.6, resize: 'vertical' }}
                    onBlur={e => e.target.value !== (activeLesson.content || '') && saveLesson(activeLesson.id, { content: e.target.value })} />
                  <span className="text-muted" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                    Supports Markdown (headers, lists, bold, italics, links, and code blocks)
                  </span>
                  <div style={{ marginTop: 6 }}>
                    <MarkdownGuide />
                  </div>
                </div>

                <div>
                  <label className="form-label" style={{ marginBottom: 6 }}>Video URL <span className="text-muted">(YouTube, Vimeo, Loom, or .mp4)</span></label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input defaultValue={activeLesson.videoUrl || ''} placeholder="https://…" style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                      onBlur={e => e.target.value !== (activeLesson.videoUrl || '') && saveLesson(activeLesson.id, { videoUrl: e.target.value })} />
                    <button className="btn btn-outline btn-sm" onClick={() => { fileInputRef.current.accept = 'video/*'; fileInputRef.current.click(); }}>
                      <Video size={15} /> Upload
                    </button>
                  </div>
                </div>

                {dripEnabled && (
                  <div>
                    <label className="form-label" style={{ marginBottom: 6 }}>Release after <span className="text-muted">(days from enrollment)</span></label>
                    <input type="number" min="0" defaultValue={activeLesson.dripDays ?? 0} style={{ ...inputStyle, width: 160 }}
                      onBlur={e => saveLesson(activeLesson.id, { dripDays: parseInt(e.target.value) || 0 })} />
                  </div>
                )}

                <div>
                  <label className="form-label" style={{ marginBottom: 6 }}>Attachments</label>
                  <div className="flex-col" style={{ gap: 6 }}>
                    {attachments.filter(a => a.lessonId === activeLesson.id).map(att => (
                      <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 9 }}>
                        <Paperclip size={14} style={{ color: 'var(--text3)' }} />
                        <a href={`${API}${att.fileUrl}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', flex: 1, fontSize: 13 }}>{att.fileName}</a>
                      </div>
                    ))}
                    <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', color: 'var(--text3)' }}
                      onClick={() => { fileInputRef.current.accept = '*/*'; fileInputRef.current.click(); }}>
                      <Plus size={14} /> Add attachment
                    </button>
                  </div>
                </div>

                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
              </div>
            </>
          ) : (
            <div className="card-body flex-center flex-col" style={{ minHeight: 320, gap: 12, color: 'var(--text3)', textAlign: 'center' }}>
              <FileText size={40} />
              <div>Select a lesson on the left to edit it,<br />or create modules &amp; lessons to build your course.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
